import { useEffect, useState } from "react";
import { Check, Globe, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Compact, persistent scan-status indicator shown in the wizard header
 * (FE change 100 — lifted out of the step content).
 *
 * Visible across every question page while extraction runs in parallel.
 * Reads the same `extractionPct` / `extractionMessage` / `isExtracting`
 * the WebSocket already drives — it does not own any new scan state.
 *
 * The smooth `displayPct` drift (avoiding 0→50→100 jumps) is preserved from
 * the original ExtractionProgress. Time-based anticipation is intentionally
 * out of scope here — see PROJECT-056.
 */
export function ScanStatus({
  pct,
  message,
  isExtracting,
  isDone,
  celebrateLine,
}: {
  pct: number;
  message: string;
  isExtracting: boolean;
  isDone: boolean;
  /** Measured "done in Xs — N% faster" line shown once the scan completes. */
  celebrateLine?: string;
}) {
  const [displayPct, setDisplayPct] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setDisplayPct((prev) => {
        const ceiling = pct >= 100 ? 100 : Math.min(pct + 3, 93);
        if (prev >= ceiling) return prev;
        return Math.min(prev + 0.7, ceiling);
      });
    }, 80);
    return () => clearInterval(id);
  }, [pct]);

  // Nothing to show before a scan has been kicked off.
  if (!isExtracting && !isDone) return null;

  return (
    <div className="flex items-center gap-2.5 min-w-0" title={message}>
      <div
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded-full border shrink-0 transition-all duration-500",
          isDone ? "bg-green-500/10 border-green-500/30" : "bg-blue-500/10 border-blue-500/20",
        )}
      >
        {isDone ? (
          <Check className="h-3 w-3 text-green-500 stroke-[2.5]" />
        ) : (
          <Globe className="h-3 w-3 text-blue-500" />
        )}
      </div>

      <div className="flex flex-col min-w-0">
        <span className="text-sm font-semibold text-foreground leading-none">
          {isDone ? "Scan complete" : "Reading your website…"}
        </span>
        <span className="text-label text-subtle-foreground leading-none mt-0.5 truncate max-w-[200px]">
          {isDone ? "Entities ready" : message || "Analysing page content…"}
        </span>
        {isDone && celebrateLine && (
          <span className="text-label-sm text-green-600/90 dark:text-green-500/90 leading-none mt-1 font-medium truncate max-w-[260px]">
            {celebrateLine}
          </span>
        )}
      </div>

      {!isDone && (
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="h-1 w-16 rounded-full bg-muted/40 overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300 ease-out"
              style={{ width: `${displayPct}%` }}
            />
          </div>
          <span className="text-label text-subtle-foreground tabular-nums w-7 text-right">
            {Math.round(displayPct)}%
          </span>
        </div>
      )}
      {!isDone && <Loader2 className="h-3 w-3 animate-spin text-subtle-foreground shrink-0" />}
    </div>
  );
}
